/**
 * Random assignment of participants to conditions — the documented algorithms.
 *
 * Every list is drawn from `seededSource(seed)` (see `random.ts`), so the seed plus the
 * inputs reproduce it exactly. Participants receive allocations in the order they are
 * listed, which should be the order they are enrolled.
 *
 * - **Simple randomisation**: each participant independently gets arm `below(k)` from the
 *   list of arms. Group sizes drift apart by chance, most noticeably in small samples.
 * - **Permuted blocks**: the list is built a block at a time. A block holds every arm
 *   (block size ÷ k) times in listed order and is then Fisher–Yates shuffled. When more
 *   than one block size is allowed, each block's size is drawn first, uniformly from the
 *   sizes given — which stops anyone predicting the end of a block. The last block may be
 *   cut short when the participants run out.
 * - **Stratified blocks**: the same, run separately within each stratum, each stratum
 *   drawing from its own generator seeded with `seed + "#" + stratum name`. A stratum's list
 *   therefore never changes when participants are added to another stratum.
 *
 * Listing an arm twice ("Treatment, Treatment, Control") gives it twice the weight: a
 * 2:1 allocation, with blocks that must then be a multiple of three.
 */

import {
  type RandomSource,
  randomBelow,
  seededSource,
  shuffle,
} from "@/lib/tool-kit/research-design/random";

export type AssignmentMethod = "simple" | "block" | "stratified";

export interface Participant {
  id: string;
  stratum: string | null;
}

export interface Allocation {
  /** 1-based position in the enrolment order. */
  sequence: number;
  id: string;
  stratum: string | null;
  /** 1-based block number (within the stratum, when stratified); null for simple. */
  block: number | null;
  arm: string;
}

export interface AssignmentPlan {
  participants: readonly Participant[];
  /** Arm labels, repeats allowed for unequal ratios. */
  arms: readonly string[];
  method: AssignmentMethod;
  blockSizes: readonly number[];
  seed: string;
}

export const MAX_PARTICIPANTS = 5000;

const LIST_SEPARATORS = /\r\n|\r|\n|,|;/;
const BLOCK_SEPARATORS = /[\s,;]+/;
const LINE_BREAK = /\r\n|\r|\n/;
const FIELD_SEPARATOR = /\t|,|;/;
const ID_HEADER =
  /^(?:id|#|participant|participant id|subject|code|name|record)$/i;

/** Arm names from a comma-, semicolon- or line-separated list. */
export function parseArms(text: string): string[] {
  return text
    .split(LIST_SEPARATORS)
    .map((arm) => arm.trim())
    .filter((arm) => arm !== "");
}

/** Block sizes from "4" or "4, 6". Unreadable entries are dropped. */
export function parseBlockSizes(text: string): number[] {
  return text
    .split(BLOCK_SEPARATORS)
    .filter((token) => token !== "")
    .map(Number)
    .filter((size) => Number.isInteger(size) && size > 0);
}

/** A block must hold every arm the same number of times. */
export function isValidBlockSize(size: number, armCount: number): boolean {
  return Number.isInteger(size) && size >= armCount && size % armCount === 0;
}

/**
 * One participant per line: an ID, optionally followed by a stratum after a tab, comma or
 * semicolon. A first line headed "ID", "Participant" and the like is skipped.
 */
export function parseParticipants(text: string): Participant[] {
  const lines = text.split(LINE_BREAK).filter((line) => line.trim() !== "");
  const rows = lines.map((line) => {
    const [id = "", stratum = ""] = line
      .split(FIELD_SEPARATOR)
      .map((field) => field.trim());
    return { id, stratum: stratum === "" ? null : stratum };
  });
  const body =
    rows.length > 0 && ID_HEADER.test(rows[0].id) ? rows.slice(1) : rows;
  return body.filter((row) => row.id !== "");
}

/** Generated IDs for a count: P001, P002, … padded to the count's width. */
export function numberedParticipants(
  count: number,
  prefix: string
): Participant[] {
  const width = Math.max(3, String(count).length);
  return Array.from({ length: count }, (_, i) => ({
    id: `${prefix}${String(i + 1).padStart(width, "0")}`,
    stratum: null,
  }));
}

/** A block-randomised sequence of `count` arms. */
export function blockSequence(
  count: number,
  arms: readonly string[],
  blockSizes: readonly number[],
  next: RandomSource
): { arm: string; block: number }[] {
  const out: { arm: string; block: number }[] = [];
  let block = 0;
  while (out.length < count) {
    const size =
      blockSizes.length === 1
        ? blockSizes[0]
        : blockSizes[randomBelow(next, blockSizes.length)];
    block += 1;
    const contents: string[] = [];
    for (let copy = 0; copy < size / arms.length; copy++) {
      contents.push(...arms);
    }
    for (const arm of shuffle(contents, next)) {
      out.push({ arm, block });
    }
  }
  return out.slice(0, count);
}

export type AssignmentProblem =
  | "no-participants"
  | "too-many-participants"
  | "too-few-arms"
  | "bad-block-size"
  | "missing-stratum";

export type AssignmentOutcome =
  | { ok: true; allocations: Allocation[] }
  | { ok: false; problem: AssignmentProblem };

function checkPlan(plan: AssignmentPlan): AssignmentProblem | null {
  if (plan.participants.length === 0) {
    return "no-participants";
  }
  if (plan.participants.length > MAX_PARTICIPANTS) {
    return "too-many-participants";
  }
  if (plan.arms.length < 2) {
    return "too-few-arms";
  }
  if (
    plan.method !== "simple" &&
    (plan.blockSizes.length === 0 ||
      !plan.blockSizes.every((size) =>
        isValidBlockSize(size, plan.arms.length)
      ))
  ) {
    return "bad-block-size";
  }
  if (
    plan.method === "stratified" &&
    plan.participants.some((participant) => participant.stratum === null)
  ) {
    return "missing-stratum";
  }
  return null;
}

export function assignParticipants(plan: AssignmentPlan): AssignmentOutcome {
  const problem = checkPlan(plan);
  if (problem) {
    return { ok: false, problem };
  }
  const { participants, arms, blockSizes, seed } = plan;
  if (plan.method === "simple") {
    const next = seededSource(seed);
    return {
      ok: true,
      allocations: participants.map((participant, i) => ({
        sequence: i + 1,
        id: participant.id,
        stratum: participant.stratum,
        block: null,
        arm: arms[randomBelow(next, arms.length)],
      })),
    };
  }
  if (plan.method === "block") {
    const sequence = blockSequence(
      participants.length,
      arms,
      blockSizes,
      seededSource(seed)
    );
    return {
      ok: true,
      allocations: participants.map((participant, i) => ({
        sequence: i + 1,
        id: participant.id,
        stratum: participant.stratum,
        block: sequence[i].block,
        arm: sequence[i].arm,
      })),
    };
  }
  const sizes = new Map<string, number>();
  for (const participant of participants) {
    const stratum = participant.stratum as string;
    sizes.set(stratum, (sizes.get(stratum) ?? 0) + 1);
  }
  const lists = new Map(
    [...sizes].map(([stratum, size]) => [
      stratum,
      blockSequence(size, arms, blockSizes, seededSource(`${seed}#${stratum}`)),
    ])
  );
  const used = new Map<string, number>();
  return {
    ok: true,
    allocations: participants.map((participant, i) => {
      const stratum = participant.stratum as string;
      const position = used.get(stratum) ?? 0;
      used.set(stratum, position + 1);
      const slot = (lists.get(stratum) ?? [])[position];
      return {
        sequence: i + 1,
        id: participant.id,
        stratum,
        block: slot.block,
        arm: slot.arm,
      };
    }),
  };
}

export interface BalanceRow {
  /** Null for the overall row. */
  stratum: string | null;
  /** Count per distinct arm, in `distinctArms` order. */
  counts: number[];
  total: number;
}

/** Each arm once, in the order first listed. */
export function distinctArms(arms: readonly string[]): string[] {
  return [...new Set(arms)];
}

/** Group sizes overall and, when strata exist, within each stratum. */
export function balanceTable(
  allocations: readonly Allocation[],
  arms: readonly string[]
): BalanceRow[] {
  const names = distinctArms(arms);
  const count = (rows: readonly Allocation[]) => ({
    counts: names.map((arm) => rows.filter((row) => row.arm === arm).length),
    total: rows.length,
  });
  const strata = [
    ...new Set(
      allocations
        .map((row) => row.stratum)
        .filter((stratum): stratum is string => stratum !== null)
    ),
  ];
  return [
    { stratum: null, ...count(allocations) },
    ...strata.map((stratum) => ({
      stratum,
      ...count(allocations.filter((row) => row.stratum === stratum)),
    })),
  ];
}
